import { useState } from 'react';
import TodayIcon from '@mui/icons-material/Today';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Alert, Grid2 as Grid } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import useFreshdeskMetrics from './hooks/use-freshdesk-metrics';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import useFreshdeskDashboard from './hooks/use-freshdesk-dashboard';
import { DashboardHero, MetricCard, SpotlightCard, TicketSection } from './components';

const FreshdeskDashboard = () => {
  const intl = useIntl();
  const [take, setTake] = useState(10);
  const { dashboard, loading, hasError, initialLoading, refresh } = useFreshdeskDashboard(take);
  const metrics = useFreshdeskMetrics(dashboard);

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: 3 }}>
      <DashboardHero dashboard={dashboard} loading={loading} take={take} onTakeChange={setTake} onRefresh={refresh} />

      {hasError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
          <FormattedMessage id="freshdesk.loadError" />
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
            <MetricCard {...metric} loading={initialLoading} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SpotlightCard summary={dashboard?.topTicketCloserToday} variant="success" loading={initialLoading} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SpotlightCard summary={dashboard?.topOpenPendingTicketAgent} variant="busy" loading={initialLoading} />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, xl: 6 }}>
          <TicketSection
            icon={<SupportAgentIcon />}
            title={intl.formatMessage({ id: 'freshdesk.sections.myTickets.title' })}
            description={intl.formatMessage({ id: 'freshdesk.sections.myTickets.description' })}
            count={dashboard?.currentAgentOpenPendingTicketCount || 0}
            tickets={dashboard?.currentAgentOpenPendingTickets || []}
            tone="primary"
            loading={initialLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, xl: 6 }}>
          <TicketSection
            icon={<PersonSearchIcon />}
            title={intl.formatMessage({ id: 'freshdesk.sections.unassigned.title' })}
            description={intl.formatMessage({ id: 'freshdesk.sections.unassigned.description' })}
            count={dashboard?.unassignedOpenPendingTicketCount || 0}
            tickets={dashboard?.unassignedOpenPendingTickets || []}
            tone="warning"
            loading={initialLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, xl: 6 }}>
          <TicketSection
            icon={<AssignmentLateIcon />}
            title={intl.formatMessage({ id: 'freshdesk.sections.overdue.title' })}
            description={intl.formatMessage({ id: 'freshdesk.sections.overdue.description' })}
            count={dashboard?.overdueOpenPendingTicketCount || 0}
            tickets={dashboard?.overdueOpenPendingTickets || []}
            tone="error"
            loading={initialLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, xl: 6 }}>
          <TicketSection
            icon={<TodayIcon />}
            title={intl.formatMessage({ id: 'freshdesk.sections.todayDue.title' })}
            description={intl.formatMessage({ id: 'freshdesk.sections.todayDue.description' })}
            count={dashboard?.todayDueOpenPendingTicketCount || 0}
            tickets={dashboard?.todayDueOpenPendingTickets || []}
            tone="info"
            loading={initialLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FreshdeskDashboard;
